import { AsyncPipe, NgClass } from '@angular/common';
import { Component } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink, RouterLinkActive } from '@angular/router';
import { AthleticLiveElasticApiService } from '@athleticnet/athletic-live-api';
import { EventSummary, MeetList, SearchRecord } from '@athleticnet/athletic-live-api-models';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgbPagination } from '@ng-bootstrap/ng-bootstrap';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  firstValueFrom,
  map,
  Observable,
  Subscription,
} from 'rxjs';
import { EventLinkService } from 'src/app/shared/services/event-link.service';
import { environment } from '../../../environments/environment';
import { EventDateComponent } from '../../event/event-date/event-date.component';
import { EventStatusComponent } from '../../event/event-status/event-status.component';
import { CurrentMeetService } from '../../shared/services/current-meet.service';
import { IsDesktopService } from '../../shared/services/is-desktop.service';

@Component({
  selector: 'app-sidebar-search',
  imports: [
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    FaIconComponent,
    NgClass,
    RouterLinkActive,
    AsyncPipe,
    EventStatusComponent,
    NgbPagination,
    EventDateComponent,
  ],
  templateUrl: './sidebar-search.component.html',
  styleUrl: './sidebar-search.component.scss',
})
export class SidebarSearchComponent {
  meetId!: number;
  meetSearchRecords!: SearchRecord[];
  searchRecordSub!: Subscription;
  searchPages: { events: EventSummary[]; searchRecords: SearchRecord[] }[] = [];
  desktop$!: Observable<boolean>;
  currentMeet$!: Observable<MeetList | null>;
  searchForm = new FormGroup({
    name: new FormControl(''),
  });
  formSub!: Subscription;
  searching: boolean = false;
  searchName: string = '';
  pageNum: number = 1;
  pageSize: number = 5;
  collectionSize: number = 0;
  placeholder = $localize`:Placeholder inviting a user to search the meet@@sidebarSearch:Search this meet...`;

  private _updateSearchRecords: BehaviorSubject<SearchRecord[]> = new BehaviorSubject<SearchRecord[]>([]);
  private readonly updateSearchRecords: Observable<SearchRecord[]> = this._updateSearchRecords.asObservable();

  constructor(
    public route: ActivatedRoute,
    private elasticApiService: AthleticLiveElasticApiService,
    private isDesktopService: IsDesktopService,
    private currentMeetService: CurrentMeetService,
    private eventLinkService: EventLinkService,
  ) {
    this.meetId = +this.route.snapshot.paramMap.get('meetId')!;
  }

  ngOnInit() {
    this.desktop$ = this.isDesktopService.isDesktop;
    this.currentMeet$ = this.currentMeetService.updateMeet;
    this.searchRecordSub = combineLatest([this.currentMeetService.updateSearchedEvents, this.updateSearchRecords])
      .pipe(
        map(([events, searchRecords]: [EventSummary[], SearchRecord[]]) => {
          let pageIdx = 0;
          const pages: { events: EventSummary[]; searchRecords: SearchRecord[] }[] = [];
          for (let i = 0, len = events.length; i < len; i += this.pageSize) {
            pages[pageIdx] = { events: [], searchRecords: [] };
            pages[pageIdx].events = events.slice(pageIdx * this.pageSize, pageIdx * this.pageSize + this.pageSize);
            if (pages[pageIdx].events.length === this.pageSize) {
              pageIdx++;
            }
          }

          let startSearchOffset = 0;
          if (pages[pageIdx] && pages[pageIdx].events.length < this.pageSize) {
            startSearchOffset = this.pageSize - pages[pageIdx].events.length;
            pages[pageIdx].searchRecords = searchRecords.slice(0, startSearchOffset);
            pageIdx++;
          }

          for (let i = 0, len = searchRecords.length; i < len; i += this.pageSize) {
            pages[pageIdx] = { events: [], searchRecords: [] };
            pages[pageIdx].searchRecords = searchRecords.slice(
              pageIdx * this.pageSize + startSearchOffset,
              pageIdx * this.pageSize + this.pageSize,
            );
            pageIdx++;
          }
          this.collectionSize = events.length + searchRecords.length;

          this.searchPages = pages;
        }),
      )
      .subscribe();

    this.formSub = this.searchForm.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(async (value) => {
        if (value.name) {
          this.searchName = value.name;
          this.currentMeetService.setCurrentMeetSearchTerm(value.name);
          await this.filterRecords(value.name?.toLowerCase());
        } else {
          this.currentMeetService.setCurrentMeetSearchTerm('');
          this._updateSearchRecords.next([]);
          this.pageNum = 1;
          this.collectionSize = 0;
        }
      });
  }

  async filterRecords(searchName?: string) {
    if (searchName && (searchName.length >= 3 || (searchName.length === 2 && !isNaN(+searchName)))) {
      this.searching = true;

      const meetSearchRecords = (
        await firstValueFrom(this.elasticApiService.search('search_record', searchName, this.meetId))
      ).filter((searchRecord) => {
        // Remove eventually. We don't want events from here
        return ['Athlete', 'Team'].indexOf(searchRecord.type) > -1;
      });
      this._updateSearchRecords.next(meetSearchRecords);

      this.searching = false;
    } else {
      this._updateSearchRecords.next([]);
    }
  }

  getEventLink(eventCategory: string, id: number, isXc: boolean) {
    const category = this.eventLinkService.getCategory(eventCategory, isXc);

    if (category) {
      return `/meets/${this.currentMeetService.meet?.id}/events/${category}/${id}`;
    }

    return '';
  }

  getLink(searchRecordType: string, id: number, isXc: boolean) {
    if (searchRecordType === 'CombinedEvent') {
      return `/meets/${this.meetId}/events/combined/${id}`;
    } else if (searchRecordType === 'IndividualEvent' && !isXc) {
      return `/meets/${this.meetId}/events/individual/${id}`;
    } else if (searchRecordType === 'IndividualEvent' && isXc) {
      return `/meets/${this.meetId}/events/xc/${id}`;
    } else if (searchRecordType === 'RelayEvent') {
      return `/meets/${this.meetId}/events/relay/${id}`;
    } else if (searchRecordType === 'Athlete') {
      return `/meets/${this.meetId}/athletes/${id}`;
    } else if (searchRecordType === 'Team') {
      return `/meets/${this.meetId}/teams/${id}`;
    }

    return '';
  }

  getQueryParams(searchRecordType: string, id: number) {
    if (searchRecordType === 'Athlete') {
      return { searchTerm: this.searchName };
    }

    return null;
  }

  clearSearch() {
    this.searchForm.setValue({ name: '' });
    this.meetSearchRecords = [];
  }

  ngOnDestroy() {
    if (this.formSub) {
      this.formSub.unsubscribe();
    }
  }

  protected readonly environment = environment;
}
